import { Head, usePage } from '@inertiajs/react'
import { escapeForScript } from '#shared/utils/safe_json_ld'

/**
 * Every tag a public page needs to be found, quoted correctly and shared.
 *
 * Built as one component because these tags only work as a set: a title with no
 * description lets Google write its own, an Open Graph block with no canonical
 * lets two URLs compete for the same page, and structured data with no sameAs
 * gives a search engine no way to connect a name to a real person. Spreading
 * them across pages by hand is how the site ended up with 17 pages and not one
 * meta description.
 *
 * SSR is enabled for this app, so everything below is present in the HTML the
 * crawler receives rather than being added later by React.
 */

const SITE_NAME = 'SOTA Navigator'

/** Falls back to the logo until a purpose-made share image exists. */
const DEFAULT_SHARE_IMAGE = '/favicons/favicon-adaptive.svg'

type SeoProps = {
  /** Page title. Rendered as-is, so it carries its own " | SOTA Navigator". */
  title: string
  /** The sentence a searcher reads under the title. Aim for 140 to 160 characters. */
  description: string
  /** Absolute or root-relative path to the share image. */
  image?: string
  /** Keep the page out of the index (thin, duplicate or private pages). */
  noindex?: boolean
  /** One or more schema.org objects. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
}

export const Seo = ({ title, description, image, noindex, jsonLd }: SeoProps) => {
  const { canonicalUrl } = usePage().props
  const shareImage = image ?? DEFAULT_SHARE_IMAGE
  const absoluteImage = shareImage.startsWith('http')
    ? shareImage
    : canonicalUrl
      ? new URL(shareImage, canonicalUrl).toString()
      : shareImage

  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []

  return (
    <Head title={title}>
      <meta name="description" content={description} head-key="description" />
      {canonicalUrl ? <link rel="canonical" href={canonicalUrl} head-key="canonical" /> : null}
      {noindex ? <meta name="robots" content="noindex, follow" head-key="robots" /> : null}

      {/* Open Graph, read by LinkedIn, Slack, WhatsApp and the rest. */}
      <meta property="og:type" content="website" head-key="og:type" />
      <meta property="og:site_name" content={SITE_NAME} head-key="og:site_name" />
      <meta property="og:title" content={title} head-key="og:title" />
      <meta property="og:description" content={description} head-key="og:description" />
      <meta property="og:image" content={absoluteImage} head-key="og:image" />
      {canonicalUrl ? <meta property="og:url" content={canonicalUrl} head-key="og:url" /> : null}

      <meta name="twitter:card" content="summary_large_image" head-key="twitter:card" />
      <meta name="twitter:title" content={title} head-key="twitter:title" />
      <meta name="twitter:description" content={description} head-key="twitter:description" />
      <meta name="twitter:image" content={absoluteImage} head-key="twitter:image" />

      {blocks.map((block, index) => (
        <script
          // eslint-disable-next-line react/no-danger
          type="application/ld+json"
          key={`ld-${index}`}
          head-key={`ld-${index}`}
          dangerouslySetInnerHTML={{ __html: escapeForScript(block) }}
        />
      ))}
    </Head>
  )
}
